import * as React from 'react';

import {formEncodedApi} from 'nutshell-core/api';

import {Button} from 'shells/button/button';
import {SelectMenu} from 'shells/select-menu/select-menu';

import styles from './peep-recurring-task-controls.module.css';

const RECURRING_PERIOD_WEEKLY_DAYS = 7;
const RECURRING_PERIOD_MONTHLY_DAYS = 30;
const RECURRING_PERIOD_QUARTLY_DAYS = 90;

type RecurringPeriod = {
    days: number;
    label: string;
    taskTitle: string;
};

const RECURRING_PERIODS: RecurringPeriod[] = [
    {
        days: RECURRING_PERIOD_WEEKLY_DAYS,
        label: 'Remind me to follow up every week',
        taskTitle: 'Keep in touch every week',
    },
    {
        days: RECURRING_PERIOD_MONTHLY_DAYS,
        label: 'Remind me to follow up every month',
        taskTitle: 'Keep in touch every month',
    },
    {
        days: RECURRING_PERIOD_QUARTLY_DAYS,
        label: 'Remind me to follow up every 3 months',
        taskTitle: 'Keep in touch every 3 months',
    },
];

function getRecurringPeriodString(recurringPeriodDays: string): string {
    switch (recurringPeriodDays) {
        case '7':
            return 'week';
        case '30':
            return 'month';
        case '90':
            return '3 months';
        default:
            return recurringPeriodDays;
    }
}

type Props = {
    model: any;
};

type State = {
    isMenuOpen: boolean;
    isModelLoading: boolean;
};

export class PeepRecurringTaskControls extends React.Component<Props, State> {
    buttonRef: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isMenuOpen: false,
            // The underlying peep backbone model needs to fetch itself,
            // and while it does, we need to display a loading state
            isModelLoading: true,
        };
    }

    componentDidMount() {
        this.props.model.on('sync', this.handleBackboneModelSyncComplete);
    }

    componentWillUnmount() {
        this.props.model.off('sync', this.handleBackboneModelSyncComplete);
    }

    render() {
        const recurringTask = this.props.model.getRecurringTask();
        const options = RECURRING_PERIODS.map((recurringPeriod) => {
            return {
                key: String(recurringPeriod.days),
                label: recurringPeriod.label,
                handleSelect: this.handleSelect(recurringPeriod),
            };
        });

        if (recurringTask) {
            options.push({
                key: '-',
                label: 'Don’t remind me to follow up',
                handleSelect: this.handleRemoveRecurringTask,
            });
        }

        return (
            <div className={styles['peep-recurring-task-controls']}>
                <Button
                    // @ts-expect-error
                    getButtonRef={(node) => {
                        this.buttonRef = node;
                    }}
                    onClick={this.handleMenuOpen}
                    variant='text-primary'
                >
                    {recurringTask
                        ? `Following up every ${getRecurringPeriodString(
                              recurringTask.get('recurringPeriod')
                          )}`
                        : undefined}
                    {!recurringTask && !this.state.isModelLoading
                        ? 'Remind me to follow up'
                        : undefined}
                </Button>
                {this.state.isMenuOpen ? (
                    <SelectMenu
                        options={options}
                        anchor={this.buttonRef}
                        location='bottom'
                        onClose={this.handleMenuClose}
                    />
                ) : undefined}
            </div>
        );
    }

    handleBackboneModelSyncComplete = () => {
        this.setState({isModelLoading: false});
    };

    handleMenuOpen = () => {
        this.setState({isMenuOpen: true});
    };

    handleMenuClose = () => {
        this.setState({isMenuOpen: false});
    };

    handleSelect = (recurringPeriod: RecurringPeriod) => () => {
        const data = {
            type: 'tasks',
            recurringPeriod: recurringPeriod.days,
            title: recurringPeriod.taskTitle,
            links: {
                relatedEntity: this.props.model.id,
            },
        } as const;

        const existingTask = this.props.model.getRecurringTask();
        if (existingTask) {
            if (existingTask.get('recurringPeriod') === recurringPeriod.days) return;

            existingTask.updateWithPut(data).done(() => {
                this.props.model.fetch();
            });
        } else {
            formEncodedApi
                .post('/rest/tasks', {
                    data: data,
                })
                .then(() => {
                    this.props.model.fetch();
                });
        }
    };

    handleRemoveRecurringTask = () => {
        const recurringTask = this.props.model.getRecurringTask();
        if (recurringTask) {
            recurringTask.destroy().done(() => {
                this.forceUpdate();
            });
        }
    };
}
